"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowLeft } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { getLenisInstance, isScrolling } from "@/lib/lenis-instance";
import { CssStarfield } from "@/components/sections/css-starfield";
import { SceneAtmosphere } from "@/components/scroll/scene-atmosphere";
import { Container } from "@/components/layout/container";
import { KineticHeadline } from "@/components/ui/kinetic-headline";
import { HtmlContent } from "@/components/ui/html-content";
import { ImageWithSkeleton } from "@/components/ui/image-with-skeleton";
import { MetaSidebar } from "@/components/ui/meta-sidebar";
import type { BlogDetail } from "@/api/blogs";

function formatDate(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

if (typeof window !== "undefined") gsap.registerPlugin(ScrollTrigger);

/**
 * Renders an admin-authored post: cover image, then the sanitized rich HTML from the editor via
 * HtmlContent (same renderer the portfolio case studies use). Shares the hero/starfield/reveal
 * treatment with the curated static post view so both read as the same page type.
 */
export function ApiBlogPostView({ post }: { post: BlogDetail }) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reveal = gsap.utils.toArray<HTMLElement>("[data-reveal]", root);
    if (reveal.length === 0) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.set(reveal, { opacity: 1, y: 0 });
      return;
    }

    const ctx = gsap.context(() => {
      reveal.forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 24 },
          {
            opacity: 1,
            y: 0,
            duration: 0.65,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none reverse" },
          }
        );
      });
    }, root);

    // Lenis caches the scrollable limit at init, before the cover image and any in-content images
    // load and grow the page. Without re-measuring, wheel/trackpad scroll clamps short of the
    // footer even though a scrollbar drag reaches it. Debounce so a burst of image loads coalesces
    // into ONE refresh — repeatedly refreshing mid-scroll snaps the Lenis scroll position and can
    // lock scrolling until reload.
    let remeasureTimeout: ReturnType<typeof setTimeout>;
    function remeasure() {
      clearTimeout(remeasureTimeout);
      remeasureTimeout = setTimeout(function run() {
        if (isScrolling()) {
          remeasureTimeout = setTimeout(run, 150);
          return;
        }
        getLenisInstance()?.resize();
        ScrollTrigger.refresh();
      }, 200);
    }
    const t = setTimeout(remeasure, 250);

    const imgs = Array.from(root.querySelectorAll("img"));
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", remeasure, { once: true });
    });

    return () => {
      clearTimeout(t);
      clearTimeout(remeasureTimeout);
      imgs.forEach((img) => img.removeEventListener("load", remeasure));
      ctx.revert();
    };
  }, [post.slug]);

  return (
    <div ref={rootRef}>
      <div className="relative overflow-hidden">
        <CssStarfield seed={post.slug.length} className="absolute inset-0 -z-10" />
        <SceneAtmosphere x="22%" y="28%" intensity={0.14} size={64} />
        <Container className="relative z-10 px-6 pt-24 sm:pt-32 pb-[5vh] lg:px-10">
          <Link
            href="/blog"
            className="inline-flex items-center gap-2 font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-text-tertiary)] transition-colors hover:text-[var(--color-primary-text)]"
          >
            <ArrowLeft aria-hidden className="size-3.5" />
            All posts
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-primary-text)]">
              {post.category}
            </span>
            <span className="font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-text-tertiary)]">
              {post.readMinutes} min read
            </span>
            <time
              dateTime={post.publishedDate}
              className="font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-text-tertiary)]"
            >
              {formatDate(post.publishedDate)}
            </time>
          </div>

          <KineticHeadline
            as="h1"
            variant="slide-up"
            lines={[post.title]}
            className="mt-5 max-w-4xl font-display text-[clamp(2rem,4.4vw,3.6rem)] font-bold leading-[1.04] tracking-[-0.02em] text-[var(--color-text-primary)]"
          />

          <p className="mt-6 max-w-[68ch] text-lg leading-relaxed text-pretty text-[var(--color-text-secondary)]">
            {post.excerpt}
          </p>
        </Container>
      </div>

      {post.coverImage && (
        <section className="relative py-8 sm:py-12">
          <Container data-reveal-group>
            <div
              data-frame
              className="relative min-h-[220px] w-full overflow-hidden rounded-lg border border-[var(--color-border)] shadow-2xl sm:min-h-[320px] sm:rounded-2xl lg:min-h-[420px]"
            >
              <ImageWithSkeleton
                data-img
                src={post.coverImage}
                alt={post.title}
                className="block h-auto w-full object-cover object-left"
              />
            </div>
          </Container>
        </section>
      )}

      <section className="relative py-8">
        <Container data-reveal-group>
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-14">
            <div data-meta className="min-w-0">
              <HtmlContent html={post.content} className="max-w-3xl" />
            </div>
            <MetaSidebar
              title="About this post"
              rows={[
                { label: "Category", value: post.category },
                { label: "Published", value: formatDate(post.publishedDate) },
                { label: "Written by", value: post.author },
              ]}
              ctaPrompt="Want us to build something like this for you?"
              ctaLabel="Start a project"
              ctaHref="/contact"
            />
          </div>
        </Container>
      </section>
    </div>
  );
}
